import { useEffect, useMemo, useState } from "react";
import { IBscResponse, IBscResult } from "../../interfaces/booking-supplier-cost";
import { BookingSupplierCostServices } from "../../services/booking-supplier-cost";
import { DEFAULT_PAGINATION } from "../../constants/pagination";
import { debounce, formatDateFieldsFlat, formatNumberFixed } from "../../utils/helper";
import { Button, Flex, Input, Table, Tooltip } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars, faFilter, faRotateRight, faSearch, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { IColumnConfig, ISortInfo } from "../../interfaces/common";
import CustomizeTableModal from "../../components/customize-table";
import { ColumnType } from "antd/es/table";
import FilterDrawer from "../../components/filter-drawer";
import { TableViewFilters } from "../../interfaces/table-view";
import TableViewDropdown from "../../components/table-view-dropdown";
import { useTableViews } from "../../hooks/useTableViews";
import dayjs from "dayjs";
import { DATE_FORMAT } from "../../constants/common";
import { useUpdateTableView } from "../../hooks/useTableView";
import FilterBscOrEsp from "./components/filter";
import { countNonEmptyFields } from "../../utils/helper";
import { useDropdownsData } from "../../hooks/useDropdownsData";
import { useNavigate } from "react-router-dom";
import { usePageStore } from "../../context/PageStoreContext";
import { useMessageApi } from "../../context/MessageContext";
import ImportClearDown from "./components/import-clear-down";
import { useAuth } from "../../context/AuthContext";

const COL_FIXED_LEFT = ["supplierCode", "supplierName"];
const TABLE_NAME = "booking_supplier_costs";

const renderFieldTable = (fieldName: string, value: any) => {
  const dateFields = [
    "exportDate",
    "bookingDate",
    "departureDate",
    "returnDate",
    "componentStartDate",
    "componentEndDate",
  ];
  const numberFields = ["bookingPax", "numberOfNights", "supplierPax", "rooms"];
  const costFields = ["localCurrencyCost", "gbpCost"];

  if (dateFields.includes(fieldName)) {
    return value ? dayjs(value).format(DATE_FORMAT) : value;
  }

  if (numberFields.includes(fieldName)) {
    return <div style={{ textAlign: "right" }}>{value}</div>;
  }

  if (costFields.includes(fieldName)) {
    return <div style={{ textAlign: "right" }}>{formatNumberFixed(value)}</div>;
  }

  if (fieldName === "pnr") {
    return value ? value?.split("/")?.join("\n") : value;
  }

  return value;
};

const setWidthFieldTable = (fieldName: string) => {
  if (["supplierName"].includes(fieldName)) {
    return 300;
  }
  if (["supplierCode", "currency"].includes(fieldName)) {
    return 100;
  }
  if (["status"].includes(fieldName)) {
    return 80;
  }
  if (["pnr"].includes(fieldName)) {
    return 270;
  }
  if (["tourName"].includes(fieldName)) {
    return 200;
  }
  if (["leadPaxName"].includes(fieldName)) {
    return 145;
  }

  if (["localCurrencyCost", "gbpCost", "supplierPax", "rooms"].includes(fieldName)) {
    return 140;
  }
  if (["parentBookingRef"].includes(fieldName)) {
    return 150;
  }

  return 120;
};

const BookingSupplierCostPage: React.FC = () => {
  const { user } = useAuth();
  const { roles = [] } = user || {};
  const isAdmin = roles[0]?.name === "Admin";
  const navigate = useNavigate();
  const messageApi = useMessageApi();
  const [listBsc, setListBsc] = useState<IBscResult[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingCalcBSC, setLoadingCalcBSC] = useState<boolean>(false);
  const [sortInfo, setSortInfo] = useState<ISortInfo>({});
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: DEFAULT_PAGINATION.pageSize,
    total: 0,
  });
  const [searchValue, setSearchValue] = useState<string>("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState<string>("");
  const [isFetchedMaterData, setIsFetchedMaterData] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);
  const [isModalImport, setIsModalImport] = useState<boolean>(false);
  const { setPageValue, getPageValue } = usePageStore();

  useEffect(() => {
    setSearchValue(getPageValue().searchValue);
    setDebouncedSearchValue(getPageValue().searchValue);
    setPagination((prev) => ({ ...prev, ...getPageValue().pagination }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    dataTableView,
    setDataTableView,
    loading: loadingTableView,
    refetchTableViews,
  } = useTableViews({
    tableName: TABLE_NAME,
  });

  const currentTableView = dataTableView?.currentView!;

  const { filters = {}, columns: columnConfigs = [] } = currentTableView || {};

  const isDefaultView = !!dataTableView?.defaultView;

  const { updateTableView } = useUpdateTableView();

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = {
        keyword: debouncedSearchValue || undefined,
        ...formatDateFieldsFlat(filters),
        page: pagination.current,
        pageSize: pagination.pageSize,
        orderBy: sortInfo.orderBy,
        orderDirection: sortInfo.orderDirection,
      };
      const response: IBscResponse = await BookingSupplierCostServices.getAll(params);
      setListBsc(response.data.results);
      setPagination((prev) => ({
        ...prev,
        total: response.data.meta?.totalElements || 0,
      }));
    } catch (error) {
      console.error("Fetch suppliers error:", error);
    } finally {
      setLoading(false);
      setIsFetchedMaterData(true);
    }
  };

  useEffect(() => {
    if (!loadingTableView) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingTableView, debouncedSearchValue, filters, pagination.current, pagination.pageSize, sortInfo]);

  const { data: dropdownsData } = useDropdownsData({ serviceType: "bscServices", enabled: isFetchedMaterData });

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearchValue(value);
        setPagination((prev) => ({ ...prev, current: 1 }));
      }, 1000),
    []
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    debouncedSearch(value);
  };

  const handleSaveCustomizeTable = async (configs: IColumnConfig[]) => {
    setDataTableView({ ...dataTableView!, currentView: { ...currentTableView, columns: configs } });
  };

  const handleTableChange = (paginationConfig: any, filters: any, sorter: any) => {
    // Handle pagination
    setPagination((prev) => ({
      ...prev,
      current: paginationConfig.pageSize !== prev.pageSize ? 1 : paginationConfig.current,
      pageSize: paginationConfig.pageSize,
    }));

    // Handle sorting
    if (sorter?.field) {
      setSortInfo({
        orderBy: sorter.field,
        orderDirection: sorter.order === "ascend" ? "asc" : "desc",
      });
    } else {
      // Clear sorting
      setSortInfo({});
    }
  };

  const handleApplyFilters = (values: TableViewFilters) => {
    const newFilters = { ...filters, ...values };
    setDataTableView({ ...dataTableView!, currentView: { ...currentTableView, filters: newFilters } });
    if (isDefaultView) {
      updateTableView({
        tableName: TABLE_NAME,
        filters: newFilters,
      });
    }
    setDebouncedSearchValue("");
    setSearchValue("");
    setOpenFilter(false);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleCalculateBSC = async (supplierCode: string, bookingRef: string) => {
    setLoadingCalcBSC(true);
    try {
      const response = await BookingSupplierCostServices.calculateBsc({ supplierCode, bookingRef });
      const { success } = response;
      if (success) {
        messageApi.success("Supplier information sync successfully");
        fetchData();
      }
    } catch (error) {
      console.error(error);
      messageApi.error("Supplier information failed to sync");
    } finally {
      setLoadingCalcBSC(false);
    }
  };

  const columns: ColumnType<IBscResult>[] = useMemo(() => {
    return columnConfigs
      .filter((col) => col.isDisplay)
      .map((col) => {
        return {
          title: col.label,
          dataIndex: col.column,
          key: col.column,
          fixed: COL_FIXED_LEFT.includes(col.column) ? "left" : false,
          sorter: true,
          sortDirections: ["ascend", "descend"],
          render: (value: any) => renderFieldTable(col.column, value),
          ...(setWidthFieldTable(col.column) && { width: setWidthFieldTable(col.column) }),
        };
      });
  }, [columnConfigs]);

  const colError: ColumnType<IBscResult> = {
    title: "",
    key: "error",
    width: 60,
    fixed: "left",
    align: "center",
    render: (_: any, record: IBscResult) => <>{contentError(record)}</>,
  };

  const contentError = (record: IBscResult) => {
    const { processErrors, supplierCode, bookingRef } = record;
    if (!processErrors?.length) return null;

    const showReloadIcon = processErrors.some((item) => item.error?.includes("does not exist in the system"));

    const content = () => (
      <ul style={{ margin: "0", padding: "5px  5px 5px 25px" }}>
        {processErrors?.map((item) => (
          <li key={item.field}>{item.error}</li>
        ))}
      </ul>
    );

    return (
      <div
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <Tooltip title={content()} placement="right">
          <FontAwesomeIcon icon={faTriangleExclamation} className="text-warning" />
        </Tooltip>
        {showReloadIcon && (
          <Tooltip title="Reload to fetch latest Supplier Info" placement="right">
            <FontAwesomeIcon
              icon={faRotateRight}
              style={{ paddingLeft: "10px", verticalAlign: "-2.6px" }}
              onClick={(e) => {
                e.stopPropagation();
                handleCalculateBSC(supplierCode, bookingRef);
              }}
            />
          </Tooltip>
        )}
      </div>
    );
  };

  const activeFilterCount = countNonEmptyFields(filters);

  const navigateEsp = (record: IBscResult) => {
    if (record.supplierCode && record.bookingRef) {
      setPageValue({
        searchValue,
        pagination,
      });
      navigate(
        `/expect-supplier-payment?params=${record.supplierCode}_${record.bookingRef}&name=${encodeURIComponent(
          record.supplierName ?? ""
        )}`
      );
    }
  };

  return (
    <div className="bsc-page">
      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <h2 className="title-page">Booking Supplier Cost</h2>
        {isAdmin && (
          <Button type="primary" onClick={() => setIsModalImport(true)}>
            Import Clear Down
          </Button>
        )}
      </Flex>
      <Flex gap={16} justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Input
          value={searchValue}
          placeholder="Enter Booking Ref., Supp. Code, Supp. Name or Lead Pax Name"
          onChange={handleInputChange}
          prefix={<FontAwesomeIcon icon={faSearch} style={{ color: "var(--text-blue-500)" }} />}
          style={{ width: 600 }}
          size="large"
          allowClear
        />
        <Flex gap={12}>
          <Button color="primary" variant="outlined" onClick={() => setShowModal(true)}>
            <FontAwesomeIcon icon={faBars} style={{ fontSize: "16px" }} />
            Customise
          </Button>
          <Button color="primary" variant="outlined" onClick={() => setOpenFilter(true)}>
            <FontAwesomeIcon icon={faFilter} style={{ fontSize: "16px" }} />
            Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
          </Button>
          <TableViewDropdown
            tableName={TABLE_NAME}
            dataTableView={dataTableView!}
            onTrigger={() => refetchTableViews()}
          />
        </Flex>
      </Flex>

      <Table
        dataSource={listBsc}
        loading={loading || loadingTableView || loadingCalcBSC}
        rowKey={(record) => record.id}
        columns={[colError, ...columns]}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `Total ${total} items`,
        }}
        onChange={handleTableChange}
        size="small"
        className="table-fm"
        rowClassName={(record, index) => {
          const classes = [];

          if (index % 2 === 0) classes.push("highlight-row");
          if (record.processErrors?.length) classes.push("error-row");

          return classes.join(" ");
        }}
        scroll={{ x: 1200 }}
        onRow={(record) => ({
          onClick: () => navigateEsp(record),
          style: { cursor: `${record.supplierCode && record.bookingRef ? "pointer" : "no-drop"}` },
        })}
      />

      {showModal && (
        <CustomizeTableModal
          open={showModal}
          onClose={() => setShowModal(false)}
          configsValue={columnConfigs}
          onSave={handleSaveCustomizeTable}
          tableName={TABLE_NAME}
          isSave={isDefaultView}
        />
      )}
      {openFilter && (
        <FilterDrawer
          title="Filter Booking Supplier Cost"
          open={openFilter}
          onClose={() => setOpenFilter(false)}
          onApply={handleApplyFilters}
          values={filters}
          children={<FilterBscOrEsp dropdownsData={dropdownsData} />}
        />
      )}
      {isModalImport && isAdmin && (
        <ImportClearDown
          isVisible={isModalImport}
          onClose={(isTrigger) => {
            setIsModalImport(false);
            if (isTrigger) {
              fetchData();
            }
          }}
        />
      )}
    </div>
  );
};

export default BookingSupplierCostPage;
